import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, message, Space, Tag, Row, Col, Card, Statistic } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const { Option } = Select;

const OnlineExpenses = () => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const [filterCategory, setFilterCategory] = useState(null);
    const [searchText, setSearchText] = useState('');

    const expenseCategories = [
        'hosting',
        'domain',
        'software',
        'marketing',
        'advertising',
        'payment-gateway',
        'support-tools',
        'design',
        'development',
        'maintenance',
        'other'
    ];

    // Fetch expenses
    const { data: expensesData, isLoading } = useQuery('online-expenses', async () => {
        const response = await api.get('/online-expenses', {
            params: { limit: 10000 }
        });
        return response.data;
    });

    // Create expense mutation
    const createMutation = useMutation(
        (values) => api.post('/online-expenses', values),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.expenseCreated'));
                setIsModalVisible(false);
                form.resetFields();
                queryClient.invalidateQueries('online-expenses');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    // Update expense mutation
    const updateMutation = useMutation(
        ({ id, values }) => api.put(`/online-expenses/${id}`, values),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.expenseUpdated'));
                setIsModalVisible(false);
                setEditingExpense(null);
                form.resetFields();
                queryClient.invalidateQueries('online-expenses');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    // Delete expense mutation
    const deleteMutation = useMutation(
        (id) => api.delete(`/online-expenses/${id}`),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.expenseDeleted'));
                queryClient.invalidateQueries('online-expenses');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    const handleSubmit = (values) => {
        const formattedValues = {
            ...values,
            date: values.date ? values.date.toISOString() : new Date().toISOString(),
            nextDueDate: values.nextDueDate ? values.nextDueDate.toISOString() : null,
        };

        if (editingExpense) {
            updateMutation.mutate({ id: editingExpense._id, values: formattedValues });
        } else {
            createMutation.mutate(formattedValues);
        }
    };

    const handleEdit = (expense) => {
        setEditingExpense(expense);
        form.setFieldsValue({
            ...expense,
            date: expense.date ? dayjs(expense.date) : dayjs(),
            nextDueDate: expense.nextDueDate ? dayjs(expense.nextDueDate) : null,
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('onlineAccounting.confirmDelete'),
            content: t('onlineAccounting.confirmDeleteExpense'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const columns = [
        {
            title: t('onlineAccounting.expenseNumber'),
            dataIndex: 'expenseNumber',
            key: 'expenseNumber',
        },
        {
            title: t('onlineAccounting.date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: t('onlineAccounting.category'),
            dataIndex: 'category',
            key: 'category',
            render: (category) => <Tag>{t(`onlineAccounting.${category}`)}</Tag>,
        },
        {
            title: t('onlineAccounting.description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('onlineAccounting.vendor'),
            key: 'vendor',
            render: (_, record) => record.vendor?.name || '-',
        },
        {
            title: t('onlineAccounting.amount'),
            key: 'amount',
            render: (_, record) => {
                return `${record.currency === 'IQD' ? 'IQD' : '$'}${record.amount?.toLocaleString() || '0'}`;
            },
        },
        {
            title: t('onlineAccounting.paymentMethod'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            render: (method) => t(`onlineAccounting.${method}`),
        },
        {
            title: t('onlineAccounting.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = status === 'paid' ? 'green' : status === 'pending' ? 'orange' : 'red';
                return <Tag color={color}>{t(`onlineAccounting.${status}`)}</Tag>;
            },
        },
        {
            title: t('onlineAccounting.actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                </Space>
            ),
        },
    ];

    const filteredData = expensesData?.data?.filter((expense) => {
        if (filterCategory && expense.category !== filterCategory) return false;
        if (searchText) {
            const search = searchText.toLowerCase();
            return (
                expense.description?.toLowerCase().includes(search) ||
                expense.expenseNumber?.toLowerCase().includes(search) ||
                expense.vendor?.name?.toLowerCase().includes(search)
            );
        }
        return true;
    });

    // Calculate statistics
    const totalExpenseUSD = expensesData?.totals?.find(t => t._id === 'USD')?.total || 0;
    const totalExpenseIQD = expensesData?.totals?.find(t => t._id === 'IQD')?.total || 0;
    const totalCount = filteredData?.length || 0;

    return (
        <div>
            <h1>{t('onlineAccounting.expenses')}</h1>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.totalExpenses')} value={totalCount} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.totalUSD')} value={totalExpenseUSD} prefix="$" precision={2} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.totalIQD')} value={totalExpenseIQD} suffix="IQD" precision={0} />
                    </Card>
                </Col>
            </Row>

            <Space style={{ marginBottom: 16 }}>
                <Input.Search
                    placeholder={t('onlineAccounting.searchExpense')}
                    allowClear
                    onSearch={setSearchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                />
                <Select
                    placeholder={t('onlineAccounting.filterByCategory')}
                    allowClear
                    onChange={setFilterCategory}
                    style={{ width: 200 }}
                >
                    {expenseCategories.map(cat => (
                        <Option key={cat} value={cat}>{t(`onlineAccounting.${cat}`)}</Option>
                    ))}
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
                    {t('onlineAccounting.addExpense')}
                </Button>
            </Space>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                loading={isLoading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editingExpense ? t('onlineAccounting.editExpense') : t('onlineAccounting.addExpense')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingExpense(null);
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="category" label={t('onlineAccounting.category')} rules={[{ required: true }]}>
                                <Select>
                                    {expenseCategories.map(cat => (
                                        <Option key={cat} value={cat}>{t(`onlineAccounting.${cat}`)}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="date" label={t('onlineAccounting.date')} initialValue={dayjs()} rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label={t('onlineAccounting.description')} rules={[{ required: true }]}>
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="amount" label={t('onlineAccounting.amount')} rules={[{ required: true }]}>
                                <Input type="number" min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="currency" label={t('onlineAccounting.currency')} initialValue="USD">
                                <Select>
                                    <Option value="USD">USD</Option>
                                    <Option value="IQD">IQD</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="paymentMethod" label={t('onlineAccounting.paymentMethod')} initialValue="online">
                                <Select>
                                    <Option value="cash">{t('onlineAccounting.cash')}</Option>
                                    <Option value="bank-transfer">{t('onlineAccounting.bank-transfer')}</Option>
                                    <Option value="card">{t('onlineAccounting.card')}</Option>
                                    <Option value="online">{t('onlineAccounting.online')}</Option>
                                    <Option value="paypal">{t('onlineAccounting.paypal')}</Option>
                                    <Option value="stripe">{t('onlineAccounting.stripe')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="status" label={t('onlineAccounting.status')} initialValue="paid">
                                <Select>
                                    <Option value="paid">{t('onlineAccounting.paid')}</Option>
                                    <Option value="pending">{t('onlineAccounting.pending')}</Option>
                                    <Option value="cancelled">{t('onlineAccounting.cancelled')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name={['vendor', 'name']} label={t('onlineAccounting.vendorName')}>
                        <Input />
                    </Form.Item>

                    <Form.Item name={['vendor', 'contact']} label={t('onlineAccounting.vendorContact')}>
                        <Input />
                    </Form.Item>

                    <Form.Item name="notes" label={t('onlineAccounting.notes')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Space>
                        <Button type="primary" htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                            {editingExpense ? t('onlineAccounting.update') : t('onlineAccounting.create')}
                        </Button>
                        <Button onClick={() => {
                            setIsModalVisible(false);
                            setEditingExpense(null);
                            form.resetFields();
                        }}>
                            {t('onlineAccounting.cancel')}
                        </Button>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};

export default OnlineExpenses;
